// reCAPTCHA v3, shared site key with businessownerscouncil.org (same Google
// console entry). The key is public by design; the paired secret lives
// server-side in eha-lambda.
const RECAPTCHA_SITE_KEY = '6Lf_j98qAAAAAJlZMY_OllkRi0D31FQyqwuNRdYx';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

let loading: Promise<void> | null = null;

// Inject the reCAPTCHA script once, on first form use, so regular page views
// never load it.
const loadRecaptcha = (): Promise<void> => {
  if (window.grecaptcha) return Promise.resolve();
  if (loading) return loading;

  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Remove the dead tag so a retry doesn't accumulate duplicates in <head>.
      script.remove();
      loading = null;
      reject(new Error('Failed to load reCAPTCHA'));
    };
    document.head.appendChild(script);
  });

  return loading;
};

export const getRecaptchaToken = async (action: string): Promise<string> => {
  await loadRecaptcha();
  return new Promise((resolve, reject) => {
    // Explicit guard instead of optional chaining: if grecaptcha is somehow
    // absent after a successful load, the promise must reject (surfacing the
    // form's failure state) rather than never settle and hang the submission.
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) {
      reject(new Error('reCAPTCHA unavailable after load'));
      return;
    }
    grecaptcha.ready(() => {
      grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve).catch(reject);
    });
  });
};
